/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import type { SpecificEventListener } from "@/types";
import { getComponentConfig, getComponentElements } from "@/utils/dom/component";
import { getElements } from "@/utils/dom/element";
import { checkFormValidity } from "@/utils/validation";
import { globalMessageList } from "./global-message-list";

const recaptchaUrl = 'https://www.google.com/recaptcha/api.js?onload=executeRecaptchaLoadCallbacks';

window.grecaptchaV2LoadCallbacks = [];
window.executeRecaptchaLoadCallbacks = function () {
    while (window.grecaptcha && window.grecaptchaV2LoadCallbacks.length > 0) {
        const callback = window.grecaptchaV2LoadCallbacks.pop();

        typeof callback === 'function' && callback();
    }
};

/**
 * Initialize recaptcha script
 */
export function loadReCaptcha(sitekey?: string) {
    if (window.grecaptcha) {
        return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;

    script.src = recaptchaUrl + '&render=' + (sitekey ? sitekey : 'explicit');
    document.head.append(script);
}

export function initReCaptcha() {
    getComponentElements(ReCaptcha.selector).forEach(element => new ReCaptcha(element));
}

const errorMessage = 'ReCaptcha validation failed, please try again';

export class ReCaptcha {
    static readonly selector = 'recaptcha';
    static readonly containerSelector = '.g-recaptcha';
    static readonly submitSelector = '[data-recaptcha-btn], [type="submit"]';
    static readonly tokenField = 'g-recaptcha-response';

    public element: HTMLElement;
    private config: ReCaptchaOptions;
    private container: HTMLElement;
    private form: HTMLFormElement | null;

    private submitListener: SpecificEventListener<'submit'>;

    private initialized: boolean = false;
    private widgetId?: string;

    constructor(element: HTMLElement, options: Partial<ReCaptchaOptions> = {}) {
        this.element = element;
        this.config = {
            ...getComponentConfig(ReCaptcha.selector, this.element),
            ...options
        };

        this.submitListener = this.config.invisible
            ? (event) => this.handleSubmitInvisible(event)
            : (event) => this.handleSubmitCheckbox(event);

        this.container = this.element.querySelector(ReCaptcha.containerSelector) || this._createContainer();
        this.form = this.element.closest('form');

        if (window.grecaptcha && window.grecaptcha.render) {
            this.initReCaptcha();
        } else {
            window.grecaptchaV2LoadCallbacks.push(() => this.initReCaptcha());
            loadReCaptcha();
        }
    }

    /**
     * Destroy instance
     */
    public destroy(): void {
        this.form?.removeEventListener('submit', this.submitListener);

        this.form = null;
        this.container = null as any;
        this.element = null as any;
    }

    /**
     * Load recaptcha script
     */
    private initReCaptcha() {
        if (!this.container || this.initialized) {
            return;
        }
        this.initialized = true;

        this.element.classList.remove('hidden');
        this.widgetId = window.grecaptcha.render(this.container, this.config.rendering || {});

        if (this.form) {
            this.form.addEventListener('submit', this.submitListener);
            getElements<HTMLButtonElement | HTMLInputElement>(ReCaptcha.submitSelector, this.form)
                .forEach(button => button.disabled = false);
        }
    }

    /**
     * Handle form submission for invisible ReCaptcha
     */
    private handleSubmitInvisible(event: SubmitEvent) {
        const form = this.form;
        if (!form) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (!form.noValidate && !checkFormValidity(form)) {
            return;
        }

        window.grecaptcha.execute(this.widgetId!, {action: 'submit'})
            .then((token: string) => {
                const tokenField = form[ReCaptcha.tokenField];
                if (token && tokenField) {
                    tokenField.value = token;
                    form.submit();
                }
                else {
                    globalMessageList.addErrorMessage(tokenField
                        ? this.config.errorMessage || errorMessage
                        : 'ReCaptcha token element is missing, please refresh the page and try again.'
                    );
                }
            })
            .catch((exception: string | Error) => {
                console.error(exception || 'An error occurred during ReCaptcha validation.');
            });
    }

    /**
     * Handle form submission for checkbox ReCaptcha
     */
    private handleSubmitCheckbox(event: SubmitEvent) {
        if (!this.form) {
            return;
        }

        if (!this.form.noValidate && !checkFormValidity(this.form)) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        const hasToken = this.form[ReCaptcha.tokenField] && this.form[ReCaptcha.tokenField].value.length > 0;
        if (!hasToken) {
            event.preventDefault();
            event.stopImmediatePropagation();

            globalMessageList.addErrorMessage(this.config.errorMessage || errorMessage);
        }
    }

    private _createContainer() {
        const element = document.createElement('div');
        element.classList.add('g-recaptcha');
        element.id = this.config.reCaptchaId || '';

        this.element.prepend(element);
        return element;
    }
}

export interface ReCaptchaOptions {
    invisible: boolean
    rendering: Record<string, any>
    reCaptchaId: string
    errorMessage?: string
}
